<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<!-- eslint-disable vue-a11y/mouse-events-have-key-events -->
<template>
	<button
		class="c-Button"
		type="button"
		:class="buttonClassObject"
		:title="title"
		:aria-label="title"
		:disabled="disabled"
		@click="emitClick"
		@mouseover="handleHoverStateIcon(true)"
		@mouseleave="handleHoverStateIcon(false)"
	>
		<Icon
			v-if="icon"
			class="c-Button__icon"
			:icon="icon"
			:icon-style="iconStyle"
		/>
		<span>{{ label }}</span>
	</button>
</template>

<script>
import Icon from '../Icon/Icon.vue'

export default {
	name: 'Button',
	components: {
		Icon
	},
	props: {
		/**
     * The Button label
     */
		label: {
			type: String,
			required: false,
			default: () => undefined
		},
		/**
     * The Button title (html title prop)
     */
		title: {
			type: String,
			required: false,
			default: () => undefined
		},
		/**
     * If the Button is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * Button type (overall look)
     * @values is-contained, is-outlined, is-text
     */
		buttonType: {
			type: String,
			required: false,
			default: () => 'is-contained',
			validator (type) {
				return [
					'is-contained',
					'is-outlined',
					'is-text'
				].includes(type)
			}
		},
		/**
     * Button style (colors)
     * @values is-primary, is-primary-inverted, is-transparent, is-added, is-important, is-alert, is-alert-dark, is-gray-800
     */
		buttonStyle: {
			type: String,
			required: false,
			default: () => 'is-primary',
			validator (style) {
				return [
					'is-primary',
					'is-primary-inverted',
					'is-transparent',
					'is-added',
					'is-important',
					'is-alert',
					'is-alert-dark',
					'is-gray-800'
				].includes(style)
			}
		},
		/**
     * Button Icon name. Depends on the Icon component, where new icons should be added as SVGs
     */
		icon: {
			type: String,
			required: false,
			default: () => undefined
		},
		/**
     * Button Icon position
     * @values left, right
     */
		iconPosition: {
			type: String,
			required: false,
			default: () => 'left',
			validator (style) {
				return [
					'left',
					'right'
				].includes(style)
			}
		}
	},
	data: () => ({
		iconStyle: ''
	}),
	computed: {
		buttonClassObject () {
			return {
				'is-contained': this.buttonType === 'is-contained',
				'is-outlined': this.buttonType === 'is-outlined',
				'is-text': this.buttonType === 'is-text',
				'is-primary': this.buttonStyle === 'is-primary',
				'is-primary-inverted': this.buttonStyle === 'is-primary-inverted',
				'is-transparent': this.buttonStyle === 'is-transparent',
				'is-added': this.buttonStyle === 'is-added',
				'is-important': this.buttonStyle === 'is-important',
				'is-alert': this.buttonStyle === 'is-alert',
				'is-alert-dark': this.buttonStyle === 'is-alert-dark',
				'is-gray-800': this.buttonStyle === 'is-gray-800',
				'has-icon-left': this.iconPosition === 'left',
				'has-icon-right': this.iconPosition === 'right',
				'has-icon-only': ((!this.label || this.label.length === 0) && this.icon)
			}
		}
	},
	watch: {
		buttonStyle: {
			handler (value) {
				if (this.disabled) {
					this.iconStyle = 'is-disabled-gray'
				} else {
					switch (value) {
						default:
						case 'is-primary':
							if (this.buttonType === 'is-contained') {
								this.iconStyle = 'is-white'
							} else {
								this.iconStyle = 'is-primary'
							}
							break
						case 'is-primary-inverted':
							this.iconStyle = 'is-primary'
							break
						case 'is-transparent':
							this.iconStyle = 'is-white'
							break
						case 'is-added':
							if (this.buttonType === 'is-contained') {
								this.iconStyle = 'is-white'
							} else {
								this.iconStyle = 'is-added'
							}
							break
						case 'is-important':
							this.iconStyle = 'is-dark-gray'
							break
						case 'is-alert':
							if (this.buttonType === 'is-contained') {
								this.iconStyle = 'is-white'
							} else {
								this.iconStyle = 'is-alert'
							}
							break
						case 'is-alert-dark':
							if (this.buttonType === 'is-contained') {
								this.iconStyle = 'is-white'
							} else {
								this.iconStyle = 'is-alert'
							}
							break
						case 'is-gray-800':
							if (this.buttonType === 'is-contained') {
								this.iconStyle = 'is-white'
							} else {
								this.iconStyle = 'is-dark-gray'
							}
							break
					}
				}
			},
			immediate: true
		}
	},
	mounted () {
		if (!this.label && !this.icon) {
			// eslint-disable-next-line no-console
			console.error('Button component must have at least a label or an icon defined!')
		}
	},
	methods: {
		emitClick () {
			if (!this.disabled) {
				/**
         * Triggers when the button is clicked
         * @event click
         */
				this.$emit('click')
			}
		},
		handleHoverStateIcon (isHovering) {
			if (!this.disabled) {
				switch (this.buttonStyle) {
					case 'is-transparent':
						this.iconStyle = isHovering ? 'is-primary' : 'is-white'
						break
					default:
						break
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-Button {
  $button-font-size: 14px;

  border: none;
  border-radius: $border-radius;
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 $base-padding;
  margin: 0;
  position: relative;
  cursor: pointer;
  transition: background-color $default-time $default-easing,
  border-color $default-time $default-easing;

  &:disabled {
    border-color: $un-n100 !important;
    background-color: $un-n100 !important;
    cursor: not-allowed;

    &::before {
      display: none;
    }

    span {
      color: $un-n500 !important;
    }
  }

  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: $border-radius;
    z-index: 1;
    display: block;
    opacity: 0;
    transition: opacity $default-time $default-easing;
  }

  &:hover::before {
    opacity: 0.08;
  }

  span {
    font-size: $button-font-size;
    font-family: $primary-font;
    font-weight: 600;
    letter-spacing: 1.25px;
    line-height: 21px;
    text-transform: uppercase;
    transition: color $default-time $default-easing;
    order: 2;
    white-space: nowrap;
  }

  &.has-icon-only {
    padding: 0 calc(#{$base-padding} / 1.5);
  }

  .c-Button__icon {
    transition: all $default-time $default-easing;
  }

  &.has-icon-left {
    .c-Button__icon {
      width: 16px !important;
      height: 16px !important;
      order: 1;
      margin-right: calc(#{$base-margin} / 2);
    }

    &.has-icon-only .c-Button__icon {
      margin: 0;
    }
  }

  &.has-icon-right {
    .c-Button__icon {
      width: 16px !important;
      height: 16px !important;
      order: 3;
      margin-left: calc(#{$base-margin} / 2);
    }

    &.has-icon-only .c-Button__icon {
      margin: 0;
    }
  }

  &.is-contained {
    box-shadow: 0px 1px 2px 0px rgba(174, 185, 203, 0.2),
    0px 1px 3px 0px rgba(174, 185, 203, 0.1);

    &.is-primary {
      border: 1px solid $un-blue;
      background-color: $un-blue;

      span {
        color: $un-white;
      }

      &::before {
        background-color: $un-white;
      }
    }

    &.is-primary-inverted {
      border: 1px solid $un-white;
      background-color: $un-white;

      span {
        color: $un-blue;
      }

      &::before {
        top: -1px;
        left: -1px;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background-color: $un-blue;
      }
    }

    &.is-transparent {
      border-color: transparent;
      background-color: transparent;
      box-shadow: none !important;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-white;
        background-color: $un-white;

        span {
          color: $un-blue;
        }
      }
    }

    &.is-added {
      border: 1px solid $un-green-dark;
      background-color: $un-green-dark;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-green-darker;
        background-color: $un-green-darker;
      }
    }

    &.is-important {
      border: 1px solid $un-yellow;
      background-color: $un-yellow;

      span {
        color: $un-gray4-light;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-yellow-dark;
        background-color: $un-yellow-dark;
      }
    }

    &.is-alert {
      border: 1px solid $un-red;
      background-color: $un-red;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-red-dark;
        background-color: $un-red-dark;
      }
    }

    &.is-alert-dark {
      border: 1px solid $un-red-dark;
      background-color: $un-red-dark;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-n900;
        background-color: $un-red-darker;
      }
    }

    &.is-gray-800 {
      border: 1px solid $un-n800;
      background-color: $un-n800;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-n900;
        background-color: $un-n900;
      }
    }
  }

  &.is-outlined {
    background-color: transparent;

    &.is-primary {
      border: 1px solid $un-blue;

      span {
        color: $un-blue;
      }

      &::before {
        background-color: $un-blue;
      }
    }

    &.is-primary-inverted {
      border: 1px solid $un-white;
      background-color: $un-white;

      span {
        color: $un-blue;
      }

      &::before {
        top: -1px;
        left: -1px;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background-color: $un-blue;
      }
    }

    &.is-transparent {
      border-color: transparent;
      background-color: transparent;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-white;
        background-color: $un-white;

        span {
          color: $un-blue;
        }
      }
    }

    &.is-added {
      border: 1px solid $un-green-dark;

      span {
        color: $un-green-dark;
      }

      &::before {
        background-color: $un-green-darker;
      }
    }

    &.is-important {
      border: 1px solid $un-yellow;

      span {
        color: $un-gray4-light;
      }

      &::before {
        background-color: $un-yellow-dark;
      }
    }

    &.is-alert {
      border: 1px solid $un-red;

      span {
        color: $un-red;
      }

      &::before {
        background-color: $un-red-dark;
      }
    }

    &.is-alert-dark {
      border: 1px solid $un-red-dark;

      span {
        color: $un-red-dark;
      }

      &::before {
        background-color: $un-red-darker;
      }
    }

    &.is-gray-800 {
      border: 1px solid $un-n800;

      span {
        color: $un-n800;
      }

      &::before {
        background-color: $un-n900;
      }
    }
  }

  &.is-text {
    background-color: transparent;

    &.is-primary {
      span {
        color: $un-blue;
      }

      &::before {
        background-color: $un-blue;
      }
    }

    &.is-primary-inverted {
      border: 1px solid $un-white;
      background-color: $un-white;

      span {
        color: $un-blue;
      }

      &::before {
        top: -1px;
        left: -1px;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background-color: $un-blue;
      }
    }

    &.is-transparent {
      border-color: transparent;
      background-color: transparent;

      span {
        color: $un-white;
      }

      &::before {
        display: none;
      }

      &:hover {
        border-color: $un-white;
        background-color: $un-white;

        span {
          color: $un-blue;
        }
      }
    }

    &.is-added {
      span {
        color: $un-green-dark;
      }

      &::before {
        background-color: $un-green-darker;
      }
    }

    &.is-important {
      span {
        color: $un-gray4-light;
      }

      &::before {
        background-color: $un-yellow-dark;
      }
    }

    &.is-alert {
      span {
        color: $un-red;
      }

      &::before {
        background-color: $un-red-dark;
      }
    }

    &.is-alert-dark {
      span {
        color: $un-red-dark;
      }

      &::before {
        background-color: $un-red-darker;
      }
    }

    &.is-gray-800 {
      span {
        color: $un-n800;
      }

      &::before {
        background-color: $un-n900;
      }
    }
  }
}
</style>
